import React, { useState } from 'react';

import { ConfirmationModal } from '@zg-rentals/rental-revenue-components';
import billingUtils from '../utils/billingUtils';

import { Box, Text, TextButton } from '@zillow/constellation-10';
import { useHistory, useParams } from 'react-router-dom';
import { useRmwPaths } from '@zg-rentals/rental-manager-env';
import type { InvoiceIdParams } from '../model/ParamTypes';
import { useAppMaintenance } from '../hooks/useMaintenance';
import { useAccountPaymentMethods } from '../hooks/useAccountPaymentMethods';

export type AutopayStatusWidgetProps = {
  accountNumber: string;
  disabled: boolean;
  hideSetup?: boolean;
};

const AutopayStatusWidget = ({ accountNumber, disabled, hideSetup = false }: AutopayStatusWidgetProps) => {
  const { isMaintenanceMode } = useAppMaintenance();
  const paths = useRmwPaths();
  const { invoiceId } = useParams<InvoiceIdParams>();
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const { removeZuoraAutoPay, setPaymentMethodLock, paymentMethodState } = useAccountPaymentMethods();
  const { isAutopayEnabled } = paymentMethodState;
  const history = useHistory();

  const cancelAutoPay = () => {
    setDisplayConfirmationModal(false);
    setPaymentMethodLock(true);
    removeZuoraAutoPay(accountNumber);
  };

  const handleCancelAutoPay = () => setDisplayConfirmationModal(true);

  const handleEditClick = () => {
    const url = `${paths.billingPage}/account/${accountNumber}`;
    const target = `${url}?prev=invoicePayment&invoiceNumber=${invoiceId}`;
    history.push(target);
  };

  const nextPaymentDate = billingUtils.getNextAutoPaymentDate();

  const renderAutopayOn = () => (
    <Box>
      <Box css={{ marginBottom: 'xs' }}>
        <Text textStyle="body-sm" css={{ fontWeight: 'semibold' }}>{`Next Payment Date: ${nextPaymentDate}`}</Text>
      </Box>
      <Text css={{ marginBottom: 'sm' }}>
        {`We will make payments to this account automatically on the next payment date.
                 Any changes for the current month must be made by ${nextPaymentDate} to avoid auto payment.`}
      </Text>
      <Box css={{ marginBlock: 'sm' }}>
        {!disabled && <TextButton onClick={handleCancelAutoPay}>Turn Autopay Off</TextButton>}
      </Box>
    </Box>
  );

  const renderAutopayOff = () => (
    <Box>
      <Text css={{ marginBottom: 'sm' }}>
        Turn on autopay after adding a payment method that can be associated with it.
      </Text>
      <Box css={{ marginBlock: 'sm' }}>
        {!disabled && !hideSetup && (
          <TextButton onClick={handleEditClick} disabled={isMaintenanceMode}>
            Set up autopay
          </TextButton>
        )}
      </Box>
    </Box>
  );

  return (
    <Box css={{ marginBlock: 'md' }}>
      <Box css={{ marginBottom: 'sm', display: 'flex', alignItems: 'center', gap: 'sm' }}>
        <Box
          css={{
            height: '12px',
            width: '12px',
            backgroundColor: isAutopayEnabled ? 'green' : '#bbb',
            borderRadius: '50%',
            display: 'inline-block',
          }}
        />
        <Text css={{ fontWeight: 'bold' }}>Automatic payments is {isAutopayEnabled ? 'ON' : 'OFF'}</Text>
      </Box>
      {isAutopayEnabled ? renderAutopayOn() : renderAutopayOff()}
      {displayConfirmationModal && (
        <ConfirmationModal
          header="Are you sure you want to cancel auto pay?"
          submitButtonTitle="Keep Auto Pay"
          cancelButtonTitle="Cancel Auto Pay"
          textContent={
            <Text>
              Auto pay lets you avoid the risk of missed payments. If you cancel, you&apos;ll have to pay manually each
              month.
            </Text>
          }
          onCancel={cancelAutoPay}
          onSubmit={() => setDisplayConfirmationModal(false)}
        />
      )}
    </Box>
  );
};

export default AutopayStatusWidget;
